// Auswahl der Variantenattribute am Variantenvater.
//
// Bewusst DOM- und DB-frei, damit die Regeln testbar sind (Muster: `lib/publish-button.ts`).
// Der Renderer im Varianten-Reiter haengt nur die Kaestchen daran.
//
// FACHLICHER HINTERGRUND: Kaufland bildet Variantengruppen algorithmisch und erwartet im
// Feed die NAMEN der Attribute, die die Gruppe ausmachen (Spalte `variant_attributes`).
// Zulaessig sind ausschliesslich Attribute der jeweiligen Kaufland-Kategorie -- ein Name aus
// einer anderen Gruppe landet zwar in der Datei, wird dort aber verworfen.

/** Kauflands Obergrenze, vom Betreiber bestaetigt. Die RPC prueft dieselbe Zahl serverseitig. */
export const MAX_VARIANT_ATTRIBUTES = 5;

/** Die Feldgruppen, deren Attribute eine Variantengruppe bilden duerfen. */
const KATEGORIE_GRUPPEN = new Set(['category_required_attributes', 'category_optional_attributes']);

/**
 * Kategorie-Attribute, die trotzdem NIE eine Variantengruppe bilden.
 *
 * Vom Betreiber am 2026-08-15 benannt. Sie kommen alle durch den Gruppenfilter, taugen aber
 * fachlich nicht:
 *   * `title`, `title_ai`, `description`, `short_description` unterscheiden sich zwangslaeufig
 *     bei jedem Geschwisterpaar — sie beschreiben die Variante, sie bilden sie nicht.
 *   * `ean` ist je Variante ohnehin verschieden; das ist der Schluessel, nicht das Merkmal.
 *   * `manufacturer`, `mpn`, `product_safety_contact` gelten fuer die ganze Familie.
 *   * `battery_disposal_instruction`, `chem_verbots_v_relevance`, `safety_note` sind
 *     Compliance-Angaben, keine Kaufentscheidung.
 *   * `rich_content`, `video`, `base_price_unit`, `real_mgb_artnum` sind Darstellung bzw.
 *     Verwaltung.
 *
 * NICHT ausgeschlossen, obwohl fachlich verwandt: `battery_construction_type`,
 * `contains_batteries`, `lithium_ion_shipping_label`, `safety_data_sheet`, `category`,
 * `additional_categories`, `picture`. Sie wurden angefragt, aber nicht bestaetigt — etwas
 * versehentlich zu verstecken waere schlechter als eine Zeile zu viel in der Liste.
 */
const NIE_VARIANTENBILDEND = new Set([
  'base_price_unit',
  'battery_disposal_instruction',
  'chem_verbots_v_relevance',
  'description',
  'ean',
  'manufacturer',
  'mpn',
  'product_safety_contact',
  'real_mgb_artnum',
  'rich_content',
  'safety_note',
  'short_description',
  'title',
  'title_ai',
  'video',
]);

export interface SelectableAttribute {
  key: string;
  label: string;
}

interface SchemaFieldLike {
  key: string;
  label: string;
  fieldGroup?: string;
}

/**
 * Die auswaehlbaren Attribute aus der Feldliste der Kategorie.
 *
 * Basisdaten (Titel, Beschreibung), Angebotsfelder (Preis, Bestand) und GPSR stehen zwar am
 * Artikel, bilden aber keine Variantengruppe. Sie zur Auswahl zu stellen waere eine Einladung
 * zum Fehler -- der Name ginge in den Feed und wuerde dort stillschweigend verworfen.
 */
export function selectableVariantAttributes(fields: readonly SchemaFieldLike[]): SelectableAttribute[] {
  return fields
    .filter((f) => f.fieldGroup !== undefined && KATEGORIE_GRUPPEN.has(f.fieldGroup))
    .filter((f) => !NIE_VARIANTENBILDEND.has(f.key))
    .map((f) => ({ key: f.key, label: f.label?.trim() ? f.label : f.key }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));
}

/**
 * Eine Zeile der Zuordnung: links die Vario-Dimension, rechts das gewaehlte Kaufland-Attribut.
 * Leerer `kaufland_name` heisst "nicht zuordnen" — ein gueltiger Zustand, denn nicht jede
 * Vario-Dimension hat eine Entsprechung bei Kaufland.
 */
export interface VariantAttributeMapEntry {
  vario_id: string;
  vario_label: string;
  kaufland_name: string;
}

/**
 * Die Namensliste fuer den Feed, abgeleitet aus der Zuordnung.
 *
 * Entdoppelt und sortiert: Der Feed darf ein Attribut nicht zweimal nennen, und dieselbe
 * Zuordnung soll immer gleich rausgehen — sonst sieht Kaufland scheinbar wechselnde
 * Vorschlaege fuer dieselbe Familie.
 */
export function mapToNames(map: readonly VariantAttributeMapEntry[]): string[] {
  const namen = new Set<string>();
  for (const e of map) {
    const n = e.kaufland_name?.trim();
    if (n) namen.add(n);
  }
  return [...namen].sort((a, b) => a.localeCompare(b));
}

export interface ToggleResult {
  selection: string[];
  /** Gesetzt, wenn die Aenderung abgelehnt wurde — Text fuer den Toast. Sonst `null`. */
  rejected: string | null;
}

/**
 * Nimmt ein Attribut auf oder entfernt es.
 *
 * Die Auswahl bleibt sortiert: Der Feed soll dieselbe Familie nicht mal in der einen und mal
 * in der anderen Reihenfolge beschreiben, sonst sieht Kaufland scheinbar wechselnde
 * Vorschlaege.
 *
 * Abwaehlen ist AUCH am Limit erlaubt -- sonst sitzt der Haendler bei fuenf Attributen fest
 * und kann keines mehr gegen ein anderes tauschen.
 */
export function toggleVariantAttribute(current: readonly string[], name: string): ToggleResult {
  const sauber = name.trim();
  if (sauber === '') return { selection: [...current], rejected: null };

  if (current.includes(sauber)) {
    return { selection: current.filter((n) => n !== sauber).sort((a, b) => a.localeCompare(b)), rejected: null };
  }
  if (current.length >= MAX_VARIANT_ATTRIBUTES) {
    return {
      selection: [...current],
      rejected: `Höchstens ${MAX_VARIANT_ATTRIBUTES} Variantenattribute — bitte zuerst eines abwählen.`,
    };
  }
  return { selection: [...current, sauber].sort((a, b) => a.localeCompare(b)), rejected: null };
}

/** Ein Balkenstand aus dem `variant-check`. */
export interface Balkenstand { filled: number; total: number }

/** Der Online-Stand einer Variantenfamilie, aus dem Kaufland-Spiegel. */
export interface OnlineStand {
  online: number;
  gesamt: number;
  /** Zeilen, zu denen der Spiegel nichts sagen konnte. */
  unbekannt: number;
  /** Nur dann darf die Variantendatei eingereicht werden. */
  alleOnline: boolean;
}

/** Das Minimum, das die Zaehlungen von einer Varianten-Zeile brauchen. */
export interface ZeileFuerZaehlung {
  isParent: boolean;
  listingId: string | null;
  pruefstatus?: 'ok' | 'kein-profil' | 'kein-listing' | 'fehler';
}

/**
 * Welche Kinder haben WIRKLICH kein Vario-Listing?
 *
 * Der Filter auf `pruefstatus !== 'fehler'` ist der Kern. Scheitert der Zeilencheck, wissen
 * wir ueber diese Variante NICHTS -- auch nicht, ob sie ein Listing hat. Sie dann unter
 * "Alle fehlenden anlegen" mitzuzaehlen behauptet einen Befund, den es nicht gibt, und
 * fuehrt zu genau dem, was der Betreiber am 2026-08-16 gemeldet hat: "Jetzt steht in den
 * Varianten Alle fehlenden anlegen (4) obwohl alle da sind."
 *
 * Zu wenig anzubieten ist hier die richtige Richtung: Ein uebersehenes Kind faellt beim
 * naechsten Laden auf, ein faelschlich angelegtes Listing muss man in Vario aufraeumen.
 */
export function fehlendeKinder<T extends ZeileFuerZaehlung>(rows: readonly T[]): T[] {
  return rows.filter((r) => !r.isParent && !r.listingId && r.pruefstatus !== 'fehler');
}

/**
 * Zaehlt, wie viele KINDER einer Familie bei Kaufland ein laufendes Angebot haben.
 *
 * Der Vater zaehlt nicht mit — er wird dort nie gelistet, er waere ein Nenner, den man
 * nie erreichen kann.
 *
 * `alleOnline` verlangt zusaetzlich, dass es ueberhaupt Kinder gibt UND dass zu keinem der
 * Stand unbekannt ist. Eine Familie ohne Kinder oder mit Luecken im Spiegel darf die
 * Einreichung nicht freischalten: Kauflands Tageslimit liegt bei 30 Feeds, und eine
 * unvollstaendige Gruppe muesste man hinterher wieder einsammeln.
 */
export function onlineStand(
  rows: ReadonlyArray<{ isParent: boolean; online?: boolean }>,
): OnlineStand {
  const kinder = rows.filter((r) => !r.isParent);
  const online = kinder.filter((r) => r.online === true).length;
  const unbekannt = kinder.filter((r) => r.online === undefined).length;
  return {
    online,
    gesamt: kinder.length,
    unbekannt,
    alleOnline: kinder.length > 0 && unbekannt === 0 && online === kinder.length,
  };
}

/**
 * Die Farbe eines Fortschrittsbalkens.
 *
 * `voll` (gruen) sobald ALLES da ist — genau das war der Wunsch des Betreibers: "Es waere
 * schoen, wenn der Balken schon gruen angezeigt wuerde sofern alle Pflichtfelder vorhanden
 * sind." Vorher richtete sich die Farbe nach `requiredGap` und blieb deshalb auch dann rot,
 * wenn die Pflichtfelder laengst vollstaendig waren.
 *
 * `leer` (grau) bei total 0: Es gibt nichts zu erfuellen. Ein gruener Balken behauptete dort
 * einen Erfolg, ein roter einen Mangel — beides waere gelogen.
 */
export function balkenFarbe(stand: Balkenstand): 'voll' | 'luecke' | 'leer' {
  if (stand.total <= 0) return 'leer';
  return stand.filled >= stand.total ? 'voll' : 'luecke';
}

/**
 * Der Warntext fuer eine Variante, der Variantenattribute fehlen — oder `null`, wenn alles da ist.
 *
 * Kaufland bildet die Gruppe algorithmisch aus genau diesen Attributen. Fehlt eines an EINEM
 * Kind, kann Kaufland dieses Kind nicht einsortieren; die Gruppe entsteht dann gar nicht oder
 * unvollstaendig. Deshalb steht die Warnung an der Zeile und nicht als Fussnote.
 *
 * Angezeigt wird der Klartext-Name aus dem Kategorie-Schema (`colour` → „Farbe"). Fehlt er,
 * bleibt der technische Schluessel stehen: besser ein sperriger Name als gar keiner.
 */
export function variantAttrWarnung(
  fehlend: readonly string[] | undefined,
  auswaehlbar: readonly SelectableAttribute[],
): string | null {
  if (!fehlend || fehlend.length === 0) return null;
  const label = new Map(auswaehlbar.map((a) => [a.key, a.label]));
  const namen = fehlend.map((k) => label.get(k) ?? k);
  return namen.length === 1
    ? `${namen[0]} fehlt — ohne diesen Wert kann Kaufland die Variante nicht einsortieren.`
    : `${namen.join(', ')} fehlen — ohne diese Werte kann Kaufland die Variante nicht einsortieren.`;
}
